<template>
  <div class="wp-root-container">
    <!-- 1. Full Page Loading Overlay -->
    <div class="wp-full-overlay" v-if="isFetching" @touchmove.prevent>
      <div class="wp-spinner"></div>
    </div>

    <!-- 2. Error Toast Message -->
    <transition name="toast-slide">
      <div class="wp-toast" v-if="isToastVisible">
        <span class="wp-toast-text">获取提示内容失败，请稍后重试</span>
        <button class="wp-toast-close" @click="hideErrorToast" aria-label="关闭">
          <!-- Circular Countdown SVG -->
          <svg class="wp-countdown-svg" viewBox="0 0 24 24">
            <circle class="wp-countdown-bg" cx="12" cy="12" r="10"></circle>
            <circle class="wp-countdown-progress" cx="12" cy="12" r="10"></circle>
          </svg>
          <!-- 'X' Icon -->
          <svg class="wp-close-icon" viewBox="0 0 24 24"><path fill="currentColor" d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/></svg>
        </button>
      </div>
    </transition>

    <!-- 3. The Warning Popup Modal -->
    <transition name="wp-fade">
      <div class="wp-overlay" v-show="localVisible" @touchmove.self.prevent>
        <div class="wp-modal">
          
          <!-- Header Section -->
          <div class="wp-header">
            <span>{{ titlePrefix }}温馨提示</span>
          </div>

          <!-- Body Section with Smooth Height Expansion -->
          <div class="wp-body is-expanded" @touchstart="onTouchStart">
            <div class="wp-body-inner">
              
              <div class="wp-content" v-if="pages.length > 0">
                <!-- Multi-page swipe -->
                <div v-if="pages.length > 1" class="wp-swipe-container">
                  <van-swipe 
                    class="wp-swipe" 
                    :autoplay="0" 
                    :loop="false"
                    indicator-color="#1989fa"
                    @change="onSwipeChange"
                    ref="swipe"
                    @touchmove.native="onTouchMove"
                    @touchend.native="onTouchEnd"
                    @touchcancel.native="onTouchEnd"
                  >
                    <van-swipe-item v-for="(page, index) in pages" :key="index">
                      <div class="wp-page-wrapper">
                        <div 
                          class="wp-page" 
                          :class="{ 
                            'is-swiping': isSwiping,
                            'content-visible': isTextVisible
                          }" 
                          ref="pages"
                          @scroll="onScroll($event, index)"
                          @touchmove="onPageTouchMove($event, index)"
                          @touchend="onPageTouchEnd($event, index)"
                          @touchcancel="onPageTouchEnd($event, index)"
                        >
                          <!-- Inner content wrapper for rubber band tracking -->
                          <div 
                            class="wp-rubber-band"
                            :class="{ 'is-snapping': pageStates[index] && pageStates[index].isSnapping }"
                            :style="{
                              transform: `translateY(${(pageStates[index] && pageStates[index].translateY) || 0}px)`
                            }"
                            v-html="page.content"
                          ></div>
                        </div>
                        
                        <!-- Fade out effect overlays -->
                        <div class="wp-fade-top" :class="{ 'is-visible': isTextVisible }" v-show="pageStates[index] && pageStates[index].hasOverflow && !pageStates[index].isAtTop"></div>
                        <div class="wp-fade-bottom" :class="{ 'is-visible': isTextVisible }" v-show="pageStates[index] && pageStates[index].hasOverflow && !pageStates[index].isAtBottom"></div>
                        
                        <!-- Custom Always-Visible Scrollbar -->
                        <div class="wp-scrollbar" :class="{ 'is-hidden': isSwiping }" v-if="pageStates[index] && pageStates[index].hasOverflow">
                          <div 
                            class="wp-scrollbar-thumb" 
                            :style="{ height: pageStates[index].thumbHeight, top: pageStates[index].thumbTop }"
                          ></div>
                        </div>
                      </div>
                    </van-swipe-item>
                  </van-swipe>
                </div>
                
                <!-- Single page -->
                <div v-else-if="pages.length === 1">
                  <div class="wp-page-wrapper is-single">
                    <div 
                      class="wp-page" 
                      :class="{ 
                        'content-visible': isTextVisible
                      }"
                      ref="pages"
                      @scroll="onScroll($event, 0)"
                      @touchmove="onPageTouchMove($event, 0)"
                      @touchend="onPageTouchEnd($event, 0)"
                      @touchcancel="onPageTouchEnd($event, 0)"
                    >
                      <!-- Inner content wrapper for rubber band tracking -->
                      <div 
                        class="wp-rubber-band"
                        :class="{ 'is-snapping': pageStates[0] && pageStates[0].isSnapping }"
                        :style="{
                          transform: `translateY(${(pageStates[0] && pageStates[0].translateY) || 0}px)`
                        }"
                        v-html="pages[0].content"
                      ></div>
                    </div>
                    
                    <!-- Fade out effect overlays -->
                    <div class="wp-fade-top" :class="{ 'is-visible': isTextVisible }" v-show="pageStates[0] && pageStates[0].hasOverflow && !pageStates[0].isAtTop"></div>
                    <div class="wp-fade-bottom" :class="{ 'is-visible': isTextVisible }" v-show="pageStates[0] && pageStates[0].hasOverflow && !pageStates[0].isAtBottom"></div>
                    
                    <!-- Custom Always-Visible Scrollbar -->
                    <div class="wp-scrollbar" v-if="pageStates[0] && pageStates[0].hasOverflow">
                      <div 
                        class="wp-scrollbar-thumb" 
                        :style="{ height: pageStates[0].thumbHeight, top: pageStates[0].thumbTop }"
                      ></div>
                    </div>
                  </div>
                </div>

              </div>

            </div>
          </div>

          <!-- Footer Section -->
          <div class="wp-footer">
            <button class="wp-close-btn" @click="handleClose">我知道了</button>
          </div>

        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import { Swipe, SwipeItem } from 'vant';
import api from '@/plugins/api';

export default {
  name: 'DepositPayMethodWarningPopup',
  components: {
    [Swipe.name]: Swipe,
    [SwipeItem.name]: SwipeItem
  },
  data() {
    return {
      titlePrefix: '', // Replaced prop with local data state
      localVisible: false,
      isFetching: false, // Controls the full-screen loading overlay
      isToastVisible: false, // Controls the error toast
      currentId: null, // Track the ID internally
      isTextVisible: false,
      pages: [],
      currentSwipeIndex: 0,
      isSwiping: false,
      startX: 0,
      startY: 0,
      lastY: 0,
      pageStates: {},
      savedScrollTop: 0,
      swipeTimer: null, // Used to reliably clear the isSwiping state
      toastTimer: null // Used to track and clear the toast countdown
    };
  },
  beforeDestroy() {
    this.unlockBackgroundScroll();
    if (this.swipeTimer) clearTimeout(this.swipeTimer);
    if (this.toastTimer) clearTimeout(this.toastTimer);
  },
  methods: {
    /**
     * PUBLIC API: Used to trigger the popup logic. 
     * Handles background fetching, loading indicators, and display conditionality.
     * @param {Number|String} id - The ID used for the API fetch
     * @param {String} prefix - Optional text to display before "温馨提示" in the header
     */
    show(id, prefix = '') {
      if (!id || id <= 0) return;
      if (this.isFetching) return; // Prevent concurrent calls

      if (this.localVisible && this.currentId === id) {
        // If it's already open with the exact same ID, reload it
        this.titlePrefix = prefix;
        this.reload();
      } else {
        // Normal open logic: Start fetching but keep modal hidden
        this.currentId = id;
        this.titlePrefix = prefix;
        this.isFetching = true;
        this.lockBackgroundScroll();
        this.fetchContent();
      }
    },

    /**
     * Internal reload logic: Transitions out, resets, and refetches
     */
    reload() {
      this.localVisible = false;
      this.unlockBackgroundScroll();

      setTimeout(() => {
        const currentPrefix = this.titlePrefix; // Preserve prefix during reload
        this.resetState();
        this.titlePrefix = currentPrefix;
        this.isFetching = true;
        this.lockBackgroundScroll();
        this.fetchContent();
      }, 300); // 300ms matches the fade-out CSS transition
    },

    /**
     * Centralized state reset to prevent data bleed between openings
     */
    resetState() {
      this.isTextVisible = false;
      this.pages = [];
      this.currentSwipeIndex = 0;
      this.isSwiping = false;
      this.pageStates = {};
      this.titlePrefix = '';
      if (this.swipeTimer) clearTimeout(this.swipeTimer);
    },

    /**
     * Internal close logic
     */
    closePopup() {
      if (!this.localVisible) return;
      this.localVisible = false;
      this.unlockBackgroundScroll();
      
      setTimeout(() => {
        this.resetState();
        this.currentId = null;
      }, 300);
    },

    /**
     * Displays the 3-second error toast from the bottom
     */
    showErrorToast() {
      this.isToastVisible = true;
      if (this.toastTimer) clearTimeout(this.toastTimer);
      
      this.toastTimer = setTimeout(() => {
        this.isToastVisible = false;
      }, 3000);
    },

    /**
     * Manually hides the error toast and clears the countdown
     */
    hideErrorToast() {
      this.isToastVisible = false;
      if (this.toastTimer) clearTimeout(this.toastTimer);
    },

    /**
     * Vant Swipe Event Handlers
     */
    onSwipeChange(index) {
      this.currentSwipeIndex = index;
    },
    
    /**
     * Mobile-safe background scroll lock.
     */
    lockBackgroundScroll() {
      if (document.body.classList.contains('wp-body-locked')) return;
      
      this.savedScrollTop = window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop;
      document.body.classList.add('wp-body-locked');
      document.documentElement.classList.add('wp-html-locked');
      
      document.body.style.position = 'fixed';
      document.body.style.width = '100%';
      document.body.style.top = `-${this.savedScrollTop}px`;
      document.body.style.overflow = 'hidden';
      document.documentElement.style.overflow = 'hidden';
    },

    unlockBackgroundScroll() {
      if (!document.body.classList.contains('wp-body-locked')) return;
      
      document.body.classList.remove('wp-body-locked');
      document.documentElement.classList.remove('wp-html-locked');
      
      document.body.style.position = '';
      document.body.style.width = '';
      document.body.style.top = '';
      document.body.style.overflow = '';
      document.documentElement.style.overflow = '';
      
      if (this.savedScrollTop > 0) window.scrollTo(0, this.savedScrollTop);
    },

    checkOverflow() {
      const refs = this.$refs.pages;
      if (!refs) return;

      const pageElements = Array.isArray(refs) ? refs : [refs];
      let newStates = {};

      pageElements.forEach((el, index) => {
        if (!el) return;
        
        const scrollHeight = el.scrollHeight;
        const clientHeight = el.clientHeight;
        const maxScroll = scrollHeight - clientHeight;
        
        const hasOverflow = scrollHeight > clientHeight + 2;
        const isAtBottom = scrollHeight - Math.round(el.scrollTop) <= clientHeight + 2;
        const isAtTop = el.scrollTop <= 0;
        
        let thumbHeight = '0%';
        let thumbTop = '0%';

        if (hasOverflow) {
          const heightPercent = Math.max((clientHeight / scrollHeight) * 100, 10);
          thumbHeight = `${heightPercent}%`;
          
          let scrollPercent = maxScroll > 0 ? el.scrollTop / maxScroll : 0;
          scrollPercent = Math.max(0, Math.min(1, scrollPercent));
          thumbTop = `${scrollPercent * (100 - heightPercent)}%`;
        }
        
        newStates[index] = { 
          hasOverflow, 
          isAtBottom, 
          isAtTop, 
          thumbHeight, 
          thumbTop,
          translateY: 0,
          isSnapping: false 
        };
      });

      this.pageStates = newStates;
    },

    onScroll(e, index) {
      if (!this.pageStates[index]) return;
      
      const el = e.target;
      const scrollHeight = el.scrollHeight;
      const clientHeight = el.clientHeight;
      const maxScroll = scrollHeight - clientHeight;

      const isAtBottom = scrollHeight - Math.round(el.scrollTop) <= clientHeight + 2;
      const isAtTop = el.scrollTop <= 0;

      let scrollPercent = maxScroll > 0 ? el.scrollTop / maxScroll : 0;
      scrollPercent = Math.max(0, Math.min(1, scrollPercent));

      this.$set(this.pageStates[index], 'isAtBottom', isAtBottom);
      this.$set(this.pageStates[index], 'isAtTop', isAtTop);

      if (this.pageStates[index].hasOverflow) {
        const heightPercent = parseFloat(this.pageStates[index].thumbHeight);
        const thumbTop = scrollPercent * (100 - heightPercent);
        this.$set(this.pageStates[index], 'thumbTop', `${thumbTop}%`);
      }
    },

    /**
     * Executes the snap-back transition on touch release
     */
    onPageTouchEnd(e, index) {
      if (this.pageStates[index] && this.pageStates[index].translateY !== 0) {
        this.$set(this.pageStates[index], 'isSnapping', true); // Enables the CSS transition
        this.$set(this.pageStates[index], 'translateY', 0);    // Springs it back to origin
      }
    },

    /**
     * Tracks overscrolling beyond the boundaries to calculate the rubber band pull
     */
    onPageTouchMove(e, index) {
      if (this.isSwiping) return; 
      if (!e.touches || e.touches.length === 0) return;

      const el = e.currentTarget;
      if (!el) return;

      const currentY = e.touches[0].clientY;
      const deltaY = currentY - this.lastY;
      this.lastY = currentY; 

      if (deltaY === 0) return;

      // Prevent rubber-band and native bounce entirely if content does not overflow
      if (this.pageStates[index] && !this.pageStates[index].hasOverflow) {
        if (e.cancelable) e.preventDefault();
        return;
      }

      const isAtTop = el.scrollTop <= 0;
      const isAtBottom = el.scrollHeight - Math.round(el.scrollTop) <= el.clientHeight + 2;

      let currentTranslateY = this.pageStates[index].translateY || 0;

      // If already in an active rubber-band state
      if (currentTranslateY !== 0) {
        if (e.cancelable) e.preventDefault(); // Stop native scrolling
        
        // Apply physics-based resistance: The further it is pulled, the stiffer it gets
        const resistance = Math.max(0.15, 1 - (Math.abs(currentTranslateY) / 120));
        let newY = currentTranslateY + (deltaY * resistance);

        // If the user pushes it back past the origin point entirely, reset to let native scroll take over
        if ((currentTranslateY > 0 && newY < 0) || (currentTranslateY < 0 && newY > 0)) {
          newY = 0;
        }

        this.$set(this.pageStates[index], 'translateY', newY);
        this.$set(this.pageStates[index], 'isSnapping', false); // Disable transition for pure 1:1 finger tracking
        return;
      }

      // Detect hitting the boundary and initiate rubber-band state
      if ((isAtTop && deltaY > 0) || (isAtBottom && deltaY < 0)) {
        if (e.cancelable) e.preventDefault();
        this.$set(this.pageStates[index], 'translateY', deltaY * 0.5);
        this.$set(this.pageStates[index], 'isSnapping', false);
      }
    },

    onTouchStart(e) {
      if (!e.touches || e.touches.length === 0) return;
      this.startX = e.touches[0].clientX;
      this.startY = e.touches[0].clientY;
      this.lastY = e.touches[0].clientY;
      this.isSwiping = false; 
    },
    onTouchMove(e) {
      if (this.isSwiping) return; 
      if (!e.touches || e.touches.length === 0) return;
      
      const currentX = e.touches[0].clientX;
      const currentY = e.touches[0].clientY;
      const diffX = Math.abs(currentX - this.startX);
      const diffY = Math.abs(currentY - this.startY);
      
      if (diffX > 5 || diffY > 5) {
        this.isSwiping = diffX > diffY;
      }
    },
    onTouchEnd() {
      if (this.isSwiping) {
        if (this.swipeTimer) clearTimeout(this.swipeTimer);
        this.swipeTimer = setTimeout(() => { this.isSwiping = false; }, 500); 
      }
    },

    async fetchContent() {
      try {
        const response = await api.getReminders(this.currentId);
        
        // Safely extract data, supporting both direct array responses and axios-style response.data wrappers
        const jsonResponse = response.data || response; 

        let result = [];
        
        // Check if the response itself is an array, or if it's nested inside a 'data' property
        if (Array.isArray(jsonResponse)) {
          result = jsonResponse;
        } else if (jsonResponse && Array.isArray(jsonResponse.data)) {
          result = jsonResponse.data;
        }

        if (Array.isArray(result) && result.length > 0) {
          // Scenario 1: Valid array with items -> Show Modal
          this.pages = result
            .slice()
            .sort((a, b) => a.position - b.position)
            .map(item => ({
              title: item.title,
              content: item.content
            }));
          this.currentSwipeIndex = 0; 
          this.isFetching = false;
          this.localVisible = true; // Modal becomes visible

          this.$nextTick(() => {
            setTimeout(() => {
              this.checkOverflow();
              setTimeout(() => {
                this.isTextVisible = true;
              }, 300);
            }, 50); 
          });
        } else {
          // Scenario 2: Empty array -> Clean up, Unlock, NO Modal
          this.pages = [];
          this.isFetching = false;
          this.unlockBackgroundScroll();
          this.resetState();
          this.currentId = null;
        }

      } catch (error) {
        // Scenario 3: Error / Timeout -> Unlock, NO Modal, Show Toast
        console.error('DepositPayMethodWarningPopup API Error:', error);
        this.isFetching = false;
        this.unlockBackgroundScroll();
        this.resetState();
        this.currentId = null;
        this.showErrorToast();
      }
    },

    handleClose() {
      this.closePopup();
      this.$emit('close');
    }
  }
};
</script>

<style scoped>
/* Loading Overlay */
.wp-full-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background-color: rgba(255, 255, 255, 0.4); /* Light overlay to block touches but show background */
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 10000;
}
.wp-spinner {
  width: 40px;
  height: 40px;
  border: 3px solid #ebedf0;
  border-top: 3px solid #1989fa;
  border-radius: 50%;
  animation: wp-spin 1s linear infinite;
}
@keyframes wp-spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* Error Toast Message */
.wp-toast {
  position: fixed;
  bottom: 60px; /* Positions toast near the bottom of the viewport */
  left: 50%;
  transform: translateX(-50%);
  background-color: rgba(0, 0, 0, 0.75);
  color: #ffffff;
  padding: 10px 14px 10px 20px; /* Slight adjustment to balance layout with button */
  border-radius: 8px;
  font-size: 14px;
  white-space: nowrap;
  pointer-events: auto; /* Required to allow clicking the close button */
  z-index: 10001;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
}

.wp-toast-close {
  position: relative;
  width: 24px;
  height: 24px;
  padding: 0;
  background: transparent;
  border: none;
  color: #ffffff;
  cursor: pointer;
  display: flex;
  justify-content: center;
  align-items: center;
  outline: none;
  opacity: 0.85;
  transition: opacity 0.2s ease;
}

.wp-toast-close:active {
  opacity: 1;
}

.wp-countdown-svg {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  transform: rotate(-90deg); /* Starts animation exactly from the top */
}

.wp-countdown-bg {
  fill: none;
  stroke: rgba(255, 255, 255, 0.2);
  stroke-width: 2;
}

.wp-countdown-progress {
  fill: none;
  stroke: #ffffff;
  stroke-width: 2;
  /* * Circumference of circle with r=10 is 2 * PI * 10 = ~62.83 
   * We set dasharray to 63 to fully cover it initially
   */
  stroke-dasharray: 63;
  stroke-dashoffset: 0;
  /* 3 seconds matches the JS setTimeout */
  animation: countdown-shrink 3s linear forwards;
}

@keyframes countdown-shrink {
  from { stroke-dashoffset: 0; }
  to { stroke-dashoffset: 63; }
}

.wp-close-icon {
  width: 14px;
  height: 14px;
  z-index: 1; /* Ensures icon is above the SVG circles */
}

.toast-slide-enter-active, .toast-slide-leave-active {
  transition: all 0.4s cubic-bezier(0.25, 0.8, 0.25, 1);
}
.toast-slide-enter, .toast-slide-leave-to {
  transform: translate(-50%, 100px); /* Slides down out of view */
  opacity: 0;
}

/* Overlay & Transitions */
.wp-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background-color: rgba(0, 0, 0, 0.6);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;
  overscroll-behavior: none; 
}

.wp-fade-enter-active, .wp-fade-leave-active { transition: opacity 0.3s ease; }
.wp-fade-enter, .wp-fade-leave-to { opacity: 0; }

/* Modal Container */
.wp-modal {
  width: 80%;
  max-width: 340px;
  background: #ffffff;
  border-radius: 12px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
}

/* Desktop Optimization for Modal Width */
@media (min-width: 768px) {
  .wp-modal { max-width: 500px; }
}

/* Header */
.wp-header {
  padding: 24px 20px 12px;
  text-align: center;
  font-size: 18px;
  font-weight: bold;
  color: #333333;
}

/* Body with Animation */
.wp-body {
  padding: 10px 20px 16px; 
  min-height: 120px;
  max-height: 140px; 
  display: flex;
  flex-direction: column;
  overflow: hidden; 
  transition: max-height 0.6s cubic-bezier(0.4, 0, 0.2, 1);
}

.wp-body.is-expanded {
  max-height: 65vh; 
}

/* Inner relative wrapper for absolute cross-fading */
.wp-body-inner {
  position: relative;
  width: 100%;
  min-height: 86px; 
}

.wp-content { width: 100%; }

/* Content & Swipe Container */
.wp-swipe-container {
  position: relative;
  width: 100%;
}

.wp-swipe {
  width: 100%; /* Restored to 100% since no arrows exist to dodge */
  margin: 0 auto;           
  min-height: 120px;
  padding-bottom: 28px; 
}

/* Targeting Vant's native pagination dots */
::v-deep .van-swipe__indicators {
  bottom: 8px; 
}
::v-deep .van-swipe__indicator {
  width: 8px;
  height: 8px;
  background-color: #d8d8d8;
  opacity: 1;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.15);
  transition: all 0.3s ease;
}
::v-deep .van-swipe__indicator--active {
  background-color: #1989fa;
  transform: scale(1.3);
}

/* Page Wrappers */
.wp-page-wrapper {
  position: relative;
  width: 100%; 
  margin: 0 auto;           
  box-sizing: border-box;
  border-radius: 8px;
  padding: 4px; 
  overflow: hidden; /* Clips the rubber band overscroll text bleed */
  transform: translateZ(0); /* Enforces border-radius clipping on iOS Safari */
}

.wp-page-wrapper.is-single {
  width: calc(100% - 24px); 
  margin-bottom: 8px;
}

/* Rendered Text Page */
.wp-page {
  font-size: 16px; /* Increased font size */
  color: #666666;
  line-height: 1.6;
  text-align: left; /* Changed from center to left by default */
  word-break: break-all;
  max-height: 50vh; 
  min-height: 100px;
  padding: 0 8px 0 4px; 
  overflow-y: auto; 
  -webkit-overflow-scrolling: touch; 
  overscroll-behavior: none; 
  opacity: 0; 
  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none; /* IE/Edge */
  /* Default transition only handles opacity */
  transition: opacity 0.5s ease;
}
.wp-page::-webkit-scrollbar { display: none; }
.wp-page.content-visible { opacity: 1; }

/* Inner Content for Rubber Band */
.wp-rubber-band {
  width: 100%;
}
.wp-rubber-band.is-snapping {
  transition: transform 0.4s cubic-bezier(0.25, 0.8, 0.25, 1);
}

/* Custom Always-Visible Scrollbar */
.wp-scrollbar {
  position: absolute;
  top: 4px; 
  bottom: 4px;
  right: 2px;
  width: 4px;
  background: transparent;
  z-index: 10; 
  border-radius: 4px;
  transition: opacity 0.3s ease; /* Fade in smoothly */
}
.wp-scrollbar.is-hidden {
  opacity: 0;
  transition: opacity 0.1s ease; /* Fade out quickly when swiping */
}

.wp-scrollbar-thumb {
  position: absolute;
  top: 0;
  right: 0;
  width: 100%;
  background-color: #87CEFA; 
  border-radius: 4px;
}

/* Top Fade Overlay Effect */
.wp-fade-top {
  position: absolute;
  top: 0; 
  left: 0;
  width: 100%; 
  height: 40px; 
  background: linear-gradient(to bottom, rgba(255, 255, 255, 1) 15%, rgba(255, 255, 255, 0) 100%);
  pointer-events: none; 
  border-top-left-radius: 8px;
  border-top-right-radius: 8px;
  z-index: 5;
  opacity: 0;
  transition: opacity 0.5s ease;
}
.wp-fade-top.is-visible { opacity: 1; }

/* Bottom Fade Overlay Effect */
.wp-fade-bottom {
  position: absolute;
  bottom: 0; 
  left: 0;
  width: 100%; 
  height: 40px; 
  background: linear-gradient(to top, rgba(255, 255, 255, 1) 15%, rgba(255, 255, 255, 0) 100%);
  pointer-events: none; 
  border-bottom-left-radius: 8px;
  border-bottom-right-radius: 8px;
  z-index: 5;
  opacity: 0;
  transition: opacity 0.5s ease;
}
.wp-fade-bottom.is-visible { opacity: 1; }

.wp-page.is-swiping { overflow-y: hidden; }

/* Footer */
.wp-footer { display: flex; width: 100%; }
.wp-close-btn {
  flex: 1;
  width: 100%;
  background: #1989fa; 
  border: none;
  padding: 18px; /* Increased padding */
  font-size: 18px; /* Increased font-size */
  font-weight: 500;
  color: #ffffff; 
  cursor: pointer;
  border-radius: 0; 
  -webkit-appearance: none; 
  outline: none;
  margin: 0;
}
.wp-close-btn:active { background-color: #1273d4; }
</style>